import {
  Box,
  Button,
  DateInput,
  Flex,
  Input,
  Spinner,
  Switch,
  Text,
  Textarea,
} from '@optiaxiom/react';
import { IconTrashCan } from '@optimizely/axiom-icons';

import type {
  AgentParameter,
  AgentParameterType,
  AgentParameterValue,
} from '@frontend/types/specialized-agent';

type ParameterInputRendererProps = {
  parameter: AgentParameter<AgentParameterType>;
  value: AgentParameterValue<AgentParameterType> | undefined;
  onChange: (value: AgentParameterValue<AgentParameterType>) => void;
  onFileUpload?: (file: File) => Promise<void>;
  isUploading?: boolean;
};

export function ParameterInputRenderer({
  parameter,
  value,
  onChange,
  onFileUpload,
  isUploading = false,
}: ParameterInputRendererProps) {
  const currentValue = value || '';

  switch (parameter.type) {
    case 'string':
      return (
        <Textarea
          value={currentValue as string}
          resize="vertical"
          rows={2}
          placeholder={parameter.description || `Enter ${parameter.name}`}
          onChange={e => onChange(e.target.value)}
        />
      );

    case 'number':
      return (
        <Input
          type="number"
          value={currentValue as number}
          placeholder={parameter.description || `Enter ${parameter.name}`}
          onChange={e => onChange(Number(e.target.value))}
        />
      );

    case 'boolean':
      return (
        <Switch checked={currentValue as boolean} onCheckedChange={checked => onChange(checked)}>
          {parameter.description || parameter.name}
        </Switch>
      );

    case 'datetime':
      return (
        <DateInput
          type="datetime-local"
          value={currentValue as string}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        />
      );

    case 'object':
      return (
        <Textarea
          value={
            typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue, null, 2)
          }
          placeholder={parameter.description || `Enter ${parameter.name} (JSON format)`}
          onChange={e => {
            try {
              const parsedValue = JSON.parse(e.target.value);
              onChange(parsedValue);
            } catch {
              onChange(e.target.value);
            }
          }}
          rows={3}
        />
      );

    case 'file': {
      const currentFileValue = currentValue as { url?: string; type?: string } | undefined;

      return (
        <Flex flexDirection="column" gap="8">
          {currentFileValue?.url && (
            <Box border="1" borderColor="border.default" rounded="md" p="12">
              <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                <Flex flexDirection="column" gap="4">
                  <Text fontSize="sm" fontWeight="500">
                    {currentFileValue.url.split('/').pop() || 'Uploaded file'}
                  </Text>
                  <Text fontSize="sm" color="fg.secondary">
                    {(currentFileValue.type || 'Unknown type').toUpperCase()}
                  </Text>
                </Flex>
                <Button
                  appearance="subtle"
                  size="sm"
                  icon={<IconTrashCan size="16" />}
                  onClick={() => onChange('' as AgentParameterValue<AgentParameterType>)}
                  aria-label="Remove file"
                />
              </Flex>
            </Box>
          )}
          <Input
            type="file"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file && onFileUpload) {
                onFileUpload(file);
              }
            }}
            disabled={isUploading}
          />
          {isUploading && (
            <Flex flexDirection="row" alignItems="center" gap="8">
              <Spinner size="sm" />
              <Text fontSize="sm" color="fg.secondary">
                Uploading...
              </Text>
            </Flex>
          )}
        </Flex>
      );
    }

    default:
      return (
        <Input
          value={currentValue as string}
          placeholder={parameter.description || `Enter ${parameter.name}`}
          onChange={e => onChange(e.target.value)}
        />
      );
  }
}
